import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import { ThaiStar, ThaiQuatrefoil, ThaiNestedMotif, ThaiDivider } from './ThaiMotif';

interface SkillCategory {
  title: string;
  icon: string;
  color: string;
  skills: { name: string; level: number }[];
}

const skillCategories: SkillCategory[] = [
  {
    title: 'Backend',
    icon: '⚙️',
    color: '#C8A24D',
    skills: [
      { name: 'Golang', level: 90 },
      { name: 'Python', level: 85 },
      { name: 'SQL', level: 85 },
      { name: 'PromQL', level: 70 },
    ],
  },
  {
    title: 'Frontend',
    icon: '🎨',
    color: '#5C3D2E',
    skills: [
      { name: 'Flutter/Dart', level: 90 },
      { name: 'TypeScript', level: 85 },
      { name: 'React', level: 85 },
      { name: 'HTML/CSS', level: 80 },
    ],
  },
  {
    title: 'Data Science',
    icon: '🧠',
    color: '#6B8E5A',
    skills: [
      { name: 'PyTorch', level: 80 },
      { name: 'TensorFlow', level: 75 },
      { name: 'sklearn', level: 80 },
      { name: 'pandas', level: 85 },
    ],
  },
  {
    title: 'DevOps & Cloud',
    icon: '☁️',
    color: '#B08E3A',
    skills: [
      { name: 'Docker', level: 85 },
      { name: 'Kubernetes', level: 75 },
      { name: 'GCP', level: 80 },
      { name: 'CI/CD', level: 80 },
    ],
  },
];

const otherSkills = [
  'Riverpod', 'Redux', 'PostgreSQL', 'MySQL', 'TimescaleDB', 'MongoDB',
  'Redis', 'Prometheus', 'ELK', 'Grafana', 'DVC', 'MLFlow', 'Jupyter',
  'Airflow', 'RabbitMQ', 'Bun & Gorm', 'pySpark', 'k6', 'ghz',
  'Postman', 'Selenium', 'BurpSuite', 'BeautifulSoup', 'geopandas',
  'folium', 'Firebase', 'Docker Swarm', 'RESTful', 'gRPC',
  'Hexagonal Architecture', 'MVVM',
];

const Skills: React.FC = () => {
  return (
    <Box
      id="skills"
      sx={{
        py: { xs: 10, md: 14 },
        background: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Thai motif decorations */}
      <ThaiQuatrefoil size={140} opacity={0.035} sx={{ top: '4%', right: '2%' }} />
      <ThaiStar size={90} opacity={0.04} rotate={-15} sx={{ bottom: '8%', left: '3%' }} />
      <ThaiNestedMotif size={60} opacity={0.04} innerColor="#FFFFFF" sx={{ top: '45%', left: '1%' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#C8A24D',
              fontWeight: 600,
              letterSpacing: 3,
              fontSize: '0.8rem',
              mb: 1.5,
              display: 'block',
            }}
          >
            SKILLS
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#2A231C' }}
          >
            My{' '}
            <Box component="span" className="serif-heading" sx={{ color: '#C8A24D' }}>
              Tech Stack
            </Box>
          </Typography>
          <ThaiDivider />
        </Box>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {skillCategories.map((category, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Box
                className="card"
                sx={{
                  p: 3,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Typography variant="h5" sx={{ fontSize: '1.4rem' }}>
                    {category.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize: '0.95rem', color: '#2A231C' }}>
                    {category.title}
                  </Typography>
                </Box>

                <Stack spacing={2.5}>
                  {category.skills.map((skill, i) => (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#2A231C' }}>
                          {skill.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>
                          {skill.level}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={skill.level}
                        sx={{
                          height: 5,
                          borderRadius: 3,
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            background: category.color,
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Other skills */}
        <Box
          className="card"
          sx={{
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: 600, fontSize: '1rem', color: '#2A231C' }}
          >
            Other Technologies & Tools
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            gap={1}
          >
            {otherSkills.map((skill, i) => (
              <Chip
                key={i}
                label={skill}
                sx={{
                  background: 'rgba(92, 61, 46, 0.06)',
                  color: '#7A6E60',
                  border: '1px solid rgba(92, 61, 46, 0.1)',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(200, 162, 77, 0.08)',
                    color: '#C8A24D',
                    borderColor: 'rgba(200, 162, 77, 0.2)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Languages */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Stack direction="row" justifyContent="center" spacing={3}>
            <Chip
              label="🇬🇧 English — Fluent"
              sx={{
                py: 2.5,
                px: 1,
                fontSize: '0.9rem',
                fontWeight: 500,
                background: '#FFFFFF',
                border: '1px solid rgba(200, 162, 77, 0.15)',
                color: '#2A231C',
              }}
            />
            <Chip
              label="🇹🇭 Thai — Fluent"
              sx={{
                py: 2.5,
                px: 1,
                fontSize: '0.9rem',
                fontWeight: 500,
                background: '#FFFFFF',
                border: '1px solid rgba(200, 162, 77, 0.15)',
                color: '#2A231C',
              }}
            />
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Skills;
